import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18n from '@/i18n';

/**
 * Get XSRF token from cookies
 * Laravel Sanctum uses XSRF-TOKEN cookie which needs to be sent as X-XSRF-TOKEN header
 */
function getXSRFToken(): string | null {
  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    const cookie = cookieArray[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return null;
}

// Determine API base URL
// Use VITE_API_URL if explicitly set to a non-localhost value
// Otherwise, derive from current page hostname (for cross-machine access)
// This allows the app to work from any IP (localhost, LAN IP, etc.)
const getBaseURL = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  // If env URL is set and not localhost, use it as-is
  if (envUrl && !envUrl.includes('localhost')) {
    return envUrl;
  }
  // Vite dev serves the SPA on :5173 while the backend answers on :80, and
  // local/jsdom origins (localhost:3000 in vitest, LAN-IP dev) likewise talk
  // to a portless local backend — those cases drop the port. Any OTHER
  // ported origin (e.g. the Tailscale admin mapping on :8443) fronts BOTH
  // the SPA and the API behind one origin, so the port must be preserved or
  // requests land on whatever service owns the portless host.
  const { protocol, hostname, host, port } = window.location;
  const isLocalHost =
    hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
  if (port === '5173' || isLocalHost) {
    return `${protocol}//${hostname}`;
  }
  return `${protocol}//${host}`;
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Required for Sanctum cookie auth
  xsrfCookieName: 'XSRF-TOKEN', // Laravel Sanctum cookie name
  xsrfHeaderName: 'X-XSRF-TOKEN', // Laravel Sanctum header name
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Manually add XSRF token to requests
    // This ensures the token is sent even if axios automatic handling fails
    const token = getXSRFToken();
    if (token && config.headers) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
    }

    // Set Accept-Language header based on user's selected language
    // This ensures backend validation messages match frontend language
    if (config.headers) {
      config.headers['Accept-Language'] = i18n.language || 'en';
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const pathname = window.location.pathname;
      // Bundle K Phase K1: portal pages redirect to /portal/login on 401.
      // Public portal pages (login, forgot, reset, invite setup) skip the redirect
      // to avoid loops while still letting the form show the error.
      if (pathname.startsWith('/portal/')) {
        const portalPublicPaths = [
          '/portal/login',
          '/portal/forgot-password',
          '/portal/reset-password/',
          '/portal/invite/',
        ];
        const isPortalPublic = portalPublicPaths.some((p) => pathname.startsWith(p));
        if (!isPortalPublic) {
          window.location.href = '/portal/login';
        }
      } else {
        // Admin paths fall through to the existing /login behavior.
        // '/forgot-password' and '/admin/reset-password' are the admin
        // password-recovery routes (frontend/src/routes/auth.tsx) — they must
        // stay public so a 401 from the auth bootstrap probe on a logged-out
        // hard load (e.g. following a reset-password email link) doesn't
        // bounce the user to /login before the recovery form can render.
        const publicPaths = ['/login', '/rate/', '/forgot-password', '/admin/reset-password'];
        const isPublicPath = publicPaths.some((path) => pathname.includes(path));
        if (!isPublicPath) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * publicApiClient — for genuinely public, unauthenticated endpoints
 * (e.g. GET /api/customer-portal-status, the kill-switch poll).
 *
 * Differs from apiClient: no Sanctum credentials, no XSRF, and no 401-redirect
 * interceptor. The 401 redirect is intentionally absent because the maintenance
 * gate polls this client — auto-redirecting on auth errors here would loop or
 * fight the gate. Callers MUST handle non-2xx responses themselves.
 *
 * Use this only for endpoints that sit outside the Sanctum-protected route
 * groups (i.e. no `auth:sanctum` middleware on the backend).
 */
const publicApiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

publicApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.headers) {
    config.headers['Accept-Language'] = i18n.language || 'en';
  }
  return config;
});

export { publicApiClient };

// CSRF cookie helper for Sanctum
export const getCsrfCookie = async (): Promise<void> => {
  await apiClient.get('/sanctum/csrf-cookie');
};

// ─── Response envelope unwrap helpers (W4 prereq — TARGET_ARCHITECTURE §4.1) ──
//
// The backend is migrating controller-by-controller from a hand-built
// `{success, data, message?}` wrapper to a bare `{data, message?}` Resource
// envelope (see docs/architecture/plans/W4_API_ENVELOPE_INVENTORY.md). These
// helpers are the SINGLE unwrap site both shapes funnel through: they read
// straight into `.data` and ignore an optional leading `success` key, so a
// controller's wrapper-drop is transparent to every caller that has migrated
// to these helpers — no frontend change needed when a backend batch merges.
//
// Only a pilot set of modules use these today (leads, tickets, tasks). Other
// modules keep reading `response.data.data` / `response.data` directly until
// their backend batch migrates (see the batch plan in the inventory doc).

/** Shared pagination metadata shape (Laravel paginator `meta`). Callers may
 * have extra keys (e.g. `from`/`to`) — those pass through untouched. */
export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  [key: string]: unknown;
}

/** A single-resource envelope: `{success?, data, message?}` or bare `{data, message?}`. */
export interface ResourceEnvelope<T> {
  success?: boolean;
  data: T;
  message?: string;
}

/** A paginated-list envelope: `{success?, data: T[], meta, links?}` or bare (no `success`). */
export interface PaginatedEnvelope<T> {
  success?: boolean;
  data: T[];
  meta: PaginationMeta;
  links?: Record<string, string | null | undefined>;
}

/**
 * Unwraps a single-resource response body, tolerant of both the legacy
 * success-wrapper shape `{success, data, message?}` and the bare-Resource
 * shape `{data, message?}`. The `success` key (if present) is stripped
 * transparently — callers never branch on it.
 *
 * Does not touch error handling: pass it only resolved (2xx) responses:
 *   const response = await apiClient.get<ResourceEnvelope<Lead>>(url);
 *   return unwrap(response);
 * A rejected request (4xx/5xx) throws before this is ever called, and that
 * rejection propagates untouched — this helper never catches.
 */
export function unwrap<T>(response: { data: ResourceEnvelope<T> }): T {
  return response.data.data;
}

/**
 * Unwraps a paginated-list response body `{data: T[], meta, links?}`,
 * tolerant of an optional leading `success` key. Returns the same
 * `{data, meta, links?}` shape the paginated types across the codebase
 * already declare (E is inferred directly from the response argument) —
 * this only strips `success` when present, it never reshapes `meta`/`links`.
 */
export function unwrapPaginated<E extends { data: unknown[]; meta: PaginationMeta }>(
  response: { data: E & { success?: boolean } }
): Omit<E, 'success'> {
  const { success: _success, ...rest } = response.data;
  return rest as Omit<E, 'success'>;
}

export default apiClient;
